/**
 * AdminFinancePage - Página principal do painel financeiro Admin
 * Dashboard de MRR com KPIs, tabelas por plano, addon e detalhamento por tenant
 * Inclui integração com Stripe para valores reais cobrados
 */

import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminFinancial } from '@/hooks/useAdminFinancial';
import { AdminFinancialKPIs } from '@/components/admin/financial/AdminFinancialKPIs';
import { AdminFinancialPlanTable } from '@/components/admin/financial/AdminFinancialPlanTable';
import { AdminFinancialAddonTable } from '@/components/admin/financial/AdminFinancialAddonTable';
import { AdminFinancialTenantTable } from '@/components/admin/financial/AdminFinancialTenantTable';

export default function AdminFinancePage() {
  const queryClient = useQueryClient();
  const {
    mrrTotal, mrrPlans, mrrAddons, payingTenants, averageTicket, revenueAtRisk,
    mrrStripe, discountTotal,
    planRevenues, addonRevenues, allPlanRevenues, allAddonRevenues,
    mrrPlansAll, mrrAddonsAll, tenantDetails,
    isLoading, isLoadingStripe, error,
  } = useAdminFinancial();

  /** Invalida cache e recarrega dados financeiros (incluindo Stripe) */
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-financial-tenants'] });
    queryClient.invalidateQueries({ queryKey: ['admin-financial-addons'] });
    queryClient.invalidateQueries({ queryKey: ['admin-financial-stripe'] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        Erro ao carregar dados financeiros: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Financeiro</h1>
          <p className="text-sm text-muted-foreground">Visão mensal de MRR (Monthly Recurring Revenue)</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <AdminFinancialKPIs
        mrrTotal={mrrTotal}
        mrrPlans={mrrPlans}
        mrrAddons={mrrAddons}
        payingTenants={payingTenants}
        averageTicket={averageTicket}
        revenueAtRisk={revenueAtRisk}
        mrrStripe={mrrStripe}
        discountTotal={discountTotal}
        isLoadingStripe={isLoadingStripe}
      />

      {/* Tabelas lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AdminFinancialPlanTable planRevenues={planRevenues} mrrPlans={mrrPlans} allPlanRevenues={allPlanRevenues} mrrPlansAll={mrrPlansAll} />
        <AdminFinancialAddonTable addonRevenues={addonRevenues} mrrAddons={mrrAddons} allAddonRevenues={allAddonRevenues} mrrAddonsAll={mrrAddonsAll} />
      </div>

      {/* Tabela detalhada por tenant */}
      <AdminFinancialTenantTable
        tenantDetails={tenantDetails}
        isLoadingStripe={isLoadingStripe}
      />
    </div>
  );
}
